// Sprint 11b.2 §2.4 — Edit a project's avatar.
//
// Live-preview WorkspaceAvatar (top), 2-character initials input, 12-color
// palette grid + Auto chip, Reset to auto, Save. Uses useProjectsStore
// for the optimistic write + multi-server broadcast (D-007 §2.5).

import { useEffect, useMemo, useRef, useState } from 'react';
// DS.7 D17 — `ds/DsModal` -> `@ds/overlays/Modal` at `size="sm"` (480 px,
// mapped from the measured `sm:max-w-md`); `@ds/data-input/TextField`'s own
// `label` prop replaces `ui/label`. `atoms/WorkspaceAvatar` stays a product
// block (DS.4 D16) and the swatch grid stays bespoke — there is no DS palette
// picker, and inventing one here would be a second source of truth against
// `ManageStagesDialog`'s identical grid.
import { DsModal } from './ds/DsModal';
import Button from '@ds/actions/Button';
import TextField from '@ds/data-input/TextField';
import { useTranslation } from '../i18n';
import { useProjectsStore } from '../stores/projects.store';
import {
  PALETTE_IDS,
  type PaletteId,
  defaultInitials,
  paletteColor,
  autoColor,
} from '../lib/avatar-defaults';
import { WorkspaceAvatar } from './atoms/WorkspaceAvatar';
import type { Project } from '../types';

const INITIALS_REGEX = /^[A-Z0-9]{2}$/;

export interface EditProjectModalProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ColorChoice = PaletteId | 'auto';

export function EditProjectModal({ project, open, onOpenChange }: EditProjectModalProps) {
  const { t } = useTranslation();
  const updateAvatar = useProjectsStore((s) => s.updateAvatar);
  const updateRepoUrl = useProjectsStore((s) => s.updateRepoUrl);
  // Sprint add-project-autoprovision (reviewer issue 2) — when the
  // project's provisioning FAILED, Save is enabled even with a
  // pristine form so "re-save the repo URL to retry" (the failure
  // copy's remediation) actually exists as a gesture. Hook must sit
  // above the `if (!project)` return (Rules of Hooks).
  const provisionFailed = useProjectsStore((s) =>
    project ? s.provisioningById[project.id]?.phase === 'failed' : false,
  );

  const [initials, setInitials] = useState('');
  const [color, setColor] = useState<ColorChoice>('auto');
  const [repoUrl, setRepoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The dialog opens on a field (see NewProjectModal for the measurement).
  const initialsRef = useRef<HTMLInputElement>(null);

  // Reseed form when the modal opens for a new project.
  useEffect(() => {
    if (!open || !project) return;
    setInitials(
      project.avatarInitials !== null
        ? project.avatarInitials
        : defaultInitials(project.name, project.id),
    );
    setColor(
      project.avatarColor !== null && (PALETTE_IDS as readonly string[]).includes(project.avatarColor)
        ? (project.avatarColor as PaletteId)
        : 'auto',
    );
    setRepoUrl(project.repoUrl ?? '');
    setSubmitting(false);
  }, [open, project]);

  // Live preview: same resolveAvatar logic the deployed avatar will use.
  // Hook MUST come before any conditional return — Rules of Hooks. The
  // initial mount of this component receives `project={null}` (the modal
  // is mounted unconditionally in WorkspaceColumn) and only acquires a
  // project on the first right-click → Edit. If `useMemo` lived below the
  // `if (!project) return null` guard, the second render would call one
  // more hook than the first and React would throw error #310.
  const previewProps = useMemo(
    () => ({
      slug: project?.id ?? '',
      name: project?.name ?? '',
      unreadCount: 0,
      isActive: false,
      avatarColor: color === 'auto' ? null : color,
      avatarInitials: initials.length === 2 ? initials : null,
      presentational: true as const,
    }),
    [color, initials, project?.id, project?.name],
  );

  if (!project) return null;

  const initialsValid = INITIALS_REGEX.test(initials);
  const baselineInitials =
    project.avatarInitials !== null
      ? project.avatarInitials
      : defaultInitials(project.name, project.id);
  const baselineColor: ColorChoice =
    project.avatarColor !== null && (PALETTE_IDS as readonly string[]).includes(project.avatarColor)
      ? (project.avatarColor as PaletteId)
      : 'auto';
  const baselineRepoUrl = project.repoUrl ?? '';
  const repoUrlDirty = repoUrl.trim() !== baselineRepoUrl;
  const repoUrlValid = !repoUrlDirty || repoUrl.trim().length > 0;
  const dirty = initials !== baselineInitials || color !== baselineColor || repoUrlDirty;
  // A failed provisioning state makes an unchanged form saveable — the
  // save IS the retry (backend re-kicks on any PATCH carrying repoUrl).
  const canSave =
    initialsValid && repoUrlValid && (dirty || provisionFailed) && !submitting;

  function handleInitialsChange(raw: string) {
    // Filter to alphanumeric + uppercase to enforce the storage contract.
    const filtered = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 2);
    setInitials(filtered);
  }

  function handleResetAuto() {
    setColor('auto');
    setInitials(defaultInitials(project!.name, project!.id));
  }

  async function handleSave() {
    if (!canSave || !project) return;
    setSubmitting(true);
    const avatarDirty = initials !== baselineInitials || color !== baselineColor;
    if (avatarDirty) {
      const autoInitials = defaultInitials(project.name, project.id);
      // Persist null when the user landed on the deterministic default —
      // that way the avatar follows future name changes without manual edits.
      const initialsToPersist = initials === autoInitials ? null : initials;
      const colorToPersist = color === 'auto' ? null : color;
      await updateAvatar(project.id, {
        avatarColor: colorToPersist,
        avatarInitials: initialsToPersist,
      });
    }
    // Sprint add-project-autoprovision — the repo URL is the repair
    // primitive: a changed URL heals wrong-repo failures, and an
    // UNCHANGED re-save while provisioning is failed is the explicit
    // retry gesture (backend kicks on any PATCH carrying repoUrl).
    if (repoUrlDirty || provisionFailed) {
      await updateRepoUrl(project.id, repoUrl.trim());
    }
    setSubmitting(false);
    onOpenChange(false);
  }

  return (
    <DsModal
      open={open}
      onClose={() => onOpenChange(false)}
      size="sm"
      title={t('workspaceCustomization.editTitle')}
      initialFocusRef={initialsRef}
      data-testid="edit-project-modal"
      footer={
        <div className="flex flex-row justify-between gap-2 w-full">
          <Button
            variant="text"
            onClick={handleResetAuto}
            disabled={submitting}
            data-testid="edit-project-reset"
          >
            {t('workspaceCustomization.resetAuto')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="edit-project-save"
          >
            {submitting
              ? t('workspaceCustomization.saving')
              : t('workspaceCustomization.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        <div className="flex justify-center">
          <WorkspaceAvatar {...previewProps} />
        </div>

        <TextField
          ref={initialsRef}
          id="edit-project-initials"
          label={t('workspaceCustomization.initials')}
          helperText={t('workspaceCustomization.initialsHint')}
          value={initials}
          onChange={(e) => handleInitialsChange(e.target.value)}
          maxLength={2}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={submitting}
          data-testid="edit-project-initials-input"
        />

        <TextField
          id="edit-project-repo-url"
          label={t('workspaceCustomization.repoUrl')}
          helperText={t('workspaceCustomization.repoUrlHint')}
          value={repoUrl}
          onChange={(e) => setRepoUrl(e.target.value)}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t('workspaceCustomization.repoUrlPlaceholder')}
          disabled={submitting}
          data-testid="edit-project-repo-url-input"
        />

        <div className="flex flex-col gap-2">
          <span className="text-caption text-muted-foreground">
            {t('workspaceCustomization.color')}
          </span>
          <div className="grid grid-cols-4 gap-2">
            {PALETTE_IDS.map((id) => {
              const swatch = paletteColor(id);
              const selected = color === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setColor(id)}
                  className="flex items-center justify-center transition-all duration-150"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius)',
                    backgroundColor: swatch.bg,
                    outline: selected ? '2px solid hsl(var(--action))' : 'none',
                    outlineOffset: selected ? '2px' : '0',
                  }}
                  aria-label={t('workspaceCustomization.colorOption', { n: id.slice(-2) })}
                  aria-pressed={selected}
                  data-testid={`edit-project-swatch-${id}`}
                  disabled={submitting}
                />
              );
            })}
            <AutoSwatch
              slug={project.id}
              selected={color === 'auto'}
              onSelect={() => setColor('auto')}
              disabled={submitting}
              ariaLabel={t('workspaceCustomization.autoColor')}
              glyph={t('workspaceCustomization.autoGlyph')}
            />
          </div>
        </div>
      </div>
    </DsModal>
  );
}

interface AutoSwatchProps {
  slug: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  ariaLabel: string;
  glyph: string;
}

function AutoSwatch({ slug, selected, onSelect, disabled, ariaLabel, glyph }: AutoSwatchProps) {
  // Diagonal split: top-left half = the deterministic auto-hue for this
  // slug, bottom-right half = muted foreground. Communicates "auto =
  // follow the system, with whatever hue currently applies".
  const auto = autoColor(slug);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="relative flex items-center justify-center transition-all duration-150 overflow-hidden"
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius)',
        outline: selected ? '2px solid hsl(var(--action))' : 'none',
        outlineOffset: selected ? '2px' : '0',
      }}
      aria-label={ariaLabel}
      aria-pressed={selected}
      data-testid="edit-project-swatch-auto"
      disabled={disabled}
    >
      <span
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${auto.bg} 0%, ${auto.bg} 50%, hsl(var(--muted)) 50%, hsl(var(--muted)) 100%)`,
        }}
        aria-hidden
      />
      <span className="relative text-caption font-550 text-white drop-shadow-sm">{glyph}</span>
    </button>
  );
}
